import { Location } from "@angular/common";
import { Component, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { AuthenticationService } from "../../../../core/authentication/authentication.service";
import { LoadingService } from "../../../../core/loader/loading.service";
import { DeleteModalComponent } from "../../../../shared/components/delete-modal/delete-modal.component";
import { DynamicFormComponent } from "../../../../shared/components/dynamic-form/dynamic-form.component";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { NotificationService } from "../../../../shared/components/notification/notification.service";
import { UserInfo } from "../../../../shared/types/user-info";
import { isRequiredField } from "../../../../shared/utils/helpers/common.helper";
import {
  CreateInternalProviderService,
  ICreateInternalForm,
} from "../create-internal-provider/create-internal-provider.service";
import { InternalProviderService } from "../internal-provider.service";
import { InternalProvider } from "../internal-provider.type";

@Component({
  selector: "app-manage-internal-provider",
  templateUrl: "./manage-internal-provider.component.html",
  styleUrls: ["./manage-internal-provider.component.css"],
  providers: [CreateInternalProviderService],
})
export class ManageInternalProviderComponent implements OnInit {
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;

  currentUser: UserInfo;
  loading = false;
  internalProvider: InternalProvider;
  isEdit: boolean;
  questions: QuestionBase<string | boolean>[];
  public isRequiredField = isRequiredField;

  constructor(
    public dialog: MatDialog,
    private loader: LoadingService,
    private route: ActivatedRoute,
    private router: Router,
    public location: Location,
    private internalProviderService: InternalProviderService,
    private questionService: CreateInternalProviderService,
    private notificationService: NotificationService,
    private authenticationService: AuthenticationService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
    this.loader.show();
  }

  ngOnInit(): void {
    const internalProviderId: string = this.route.snapshot.paramMap.get("id");
    this.getInternalProvider(internalProviderId);
  }

  goBack(): void {
    this.location.back();
  }

  getInternalProvider(id: string): Subscription {
    return this.internalProviderService.getInternalProvider(id).subscribe(
      (response: InternalProvider) => {
        this.internalProvider = response;
        this.buildForm(response);
      },
      (error) => {
        this.notificationService.error(error || "Couldn't find Internal Provider");
        this.router.navigate(["/internal-providers"]);
      },
    );
  }

  buildForm(provider: InternalProvider): void {
    this.questionService
      .generateQuestions({
        nameValue: provider.name,
        emailValue: provider.email,
        phoneValue: provider.phone,
        enabledValue: provider.enabled,
      })
      .subscribe((qs) => {
        this.questions = qs;
        this.loader.hide();
      });
  }

  toggleManageConfig(): void {
    this.isEdit = !this.isEdit;
  }

  onSubmit(values: ICreateInternalForm): void {
    this.loader.show();
    this.internalProviderService
      .updateInternalProvider(this.internalProvider.id, { ...values })
      .pipe(finalize(() => this.dynamicForm.enable()))
      .subscribe(
        (internalProvider): void => {
          this.notificationService.success("Internal provider successfully edited");
          this.internalProvider = internalProvider;
          this.toggleManageConfig();
          this.loader.hide();
        },
        (error) => {
          this.loader.hide();
          this.notificationService.error(error?.message ?? "Could not create internal provider");
        },
      );
  }

  deleteConfigModal(): void {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "internal provider",
        data: this.internalProvider,
        overwrittenUrl: `${this.internalProviderService.basePath}/${this.internalProvider.id}`,
        warningEntity: "internalProvider",
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.router.navigate(["/internal-providers"]);
      }
    });
  }
}
